import { FC, ReactNode, memo, Key, useRef, } from 'react';
// 组件
import { ProTable, ProTooltip } from '../../antd';
// 方法
import classnames from 'classnames';
import { moneyFormat, accAdd } from '../../utils/dataUtils';
// 常量
import { columnsProps } from '../interface';

export interface BaseTableSubTotalProps {
  subTotalKeys?: string[] | false; // 需要小计的字段
  subTotalIndex?: string; // 小计在哪一列显示
  subTotalText?: ReactNode; // 小计文字
}
export interface TableSubTotalProps extends BaseTableSubTotalProps {
  pageData?: any[];
  columns?: columnsProps[];
  hasSelect?: boolean;
  tableRef?: any;
}
/** 表格小计 */
const TableSubTotal: FC<TableSubTotalProps> = props => {
  const {
    pageData,
    columns,
    subTotalKeys,
    subTotalIndex,
    subTotalText,
    hasSelect,
    tableRef,
  } = props;
  const columnPointer = useRef<number>(0);
  if (!columns) return null;
  if (!subTotalKeys) return null;
  if (!pageData || pageData.length === 0) return null;
  const defSubTotalIndex = subTotalIndex || columns[0].dataIndex;
  const subTotalData: any = {};
  pageData.forEach(item => {
    subTotalKeys.forEach(v => {
      if (!subTotalData[v]) {
        subTotalData[v] = item[v] || 0;
      } else {
        subTotalData[v] = accAdd(subTotalData[v], item[v] || 0);
      }
    });
  });
  const newColumns = [...columns];
  if (hasSelect) newColumns.unshift({ width: 369 });
  const renderValue = (column: columnsProps) => {
    const { dataIndex } = column;
    if (dataIndex === defSubTotalIndex) return subTotalText;
    if (dataIndex && typeof dataIndex === 'string') {
      const columnText = subTotalData[dataIndex];
      if (columnText !== undefined) {
        return (
          <ProTooltip title={columnText}>
            {formatValue(columnText, column)}
          </ProTooltip>
        );
      }
      return '';
    }
    return '';
  };
  const formatValue = (value: any, column: columnsProps) => {
    const { renderType, moneyProps = {} } = column;
    if (renderType === 'money') {
      const { color, fontWeight, fontSize } = moneyProps;
      return (
        <span style={{ color, fontWeight, fontSize }}>
          {moneyFormat(value, moneyProps)}
        </span>
      );
    }
    return value;
  };
  /**
   * 生成小计列
   * @param renderColumns 列配置项集合
   * @param isResetPointer 是否重置列指针
   * @returns 
   */
  const renderCell = (
    renderColumns: columnsProps[],
    isResetPointer?: boolean,
  ): ReactNode[] => {
    const newCell: ReactNode[] = [];
    if (isResetPointer) columnPointer.current = 0;
    renderColumns?.forEach((column) => {
      const { render, ellipsis, children, ...resColumnProps } = column;
      const classes = classnames('ant-table-summary-td', {
        'yb-line-ellipsis': !!ellipsis,
      });
      if (Array.isArray(children) && children.length) {
        const childrenCell = renderCell(children, false);
        return newCell.push(...childrenCell);
      }
      newCell.push(
        <ProTable.Summary.Cell key={columnPointer.current} index={columnPointer.current} {...resColumnProps}>
          <div className={classes}>{renderValue(column)}</div>
        </ProTable.Summary.Cell>
      );
      columnPointer.current = columnPointer.current + 1;
    });
    return newCell;
  };
  const renderHeadCell = (
    renderColumns: columnsProps[],
    parentKey?: Key,
  ): ReactNode[] => {
    const newHealCell: ReactNode[] = [];
    renderColumns?.forEach((column, index) => {
      const currentKey: Key = parentKey ? `${parentKey}_${index}` : index;
      const { width, children } = column;
      if (Array.isArray(children) && children.length) {
        const childrenHeadCell = renderHeadCell(children, currentKey);
        return newHealCell.push(...childrenHeadCell);
      }
      if (index === 0 && width === 369) {
        newHealCell.push(<col key={index} className="ant-table-selection-col" />);
      } else {
        newHealCell.push(<col key={currentKey} style={{ width: width, minWidth: width }} />);
      }
    });
    return newHealCell;
  };

  return (
    <table className="an-table yb-table-total" style={{ tableLayout: 'fixed' }}>
      <colgroup>
        {renderHeadCell(newColumns)}
      </colgroup>
      <tbody className="ant-table-tbody">
        <ProTable.Summary.Row className="ant-table-row ant-table-row-level-0">
          {renderCell(newColumns, true)}
        </ProTable.Summary.Row>
      </tbody>
    </table>
  );
};

TableSubTotal.defaultProps = {
  subTotalKeys: false,
  subTotalText: '小计',
  hasSelect: false,
};
export default TableSubTotal;
